// @flow
import React, {useContext, useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ErrorPopup from "../../GUI_COMMON/COMPONENTS/ErrorPopup";
import CheckInput from "../../GUI_COMMON/COMPONENTS/CheckInput";
import TextArea from "../../GUI_COMMON/COMPONENTS/TextArea";
import {StoreContext} from "../../GUI_COMMON/SUPPORT/store";
import CommonClientApi from "../../GUI_COMMON/API/commonClientApi";
import ArchiverClientApi from "../../API/archiverClientApi";
import {gotAddNewProfile, gotRemoveProfile, gotUpdateProfile} from "../../ACTIONS/clientActions";
import {gotMagTypes, gotSampleTypes} from "../../GUI_COMMON/ACTIONS/commonClientActions";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import ImageSetterCard from "../ImageSetterCard";
import {ImageInfoStruct} from "../ImageInfoStruct";
import LIUtils from "../../GUI_COMMON/SUPPORT/LIUtils";

const ProfileAdminPage = () => {

    /**
     * these are hardcoded into the archiver -- don't change
     */
    const PROFILE_TYPE_MAG=0;
    const PROFILE_TYPE_SAMPLE=1;
    const PROFILE_TYPES = [
        {type:PROFILE_TYPE_MAG,name:"MAG"},
        {type:PROFILE_TYPE_SAMPLE,name:"SAMPLE"},
    ];

    const [globalState, dispatch] = useContext(StoreContext);
    
    const [profileTab, setProfileTab] = useState("magprofiles");
    const [curProfileDetails, setCurProfileDetails] = useState(getDefaultProfileDetails());
    const [fullProfilesList, setFullProfilesList] = useState([]);
    const [magProfilesList, setMagProfilesList] = useState([]);
    const [sampleProfilesList, setSampleProfilesList] = useState([]);
    const [magTypes, setMagTypes] = useState([]);
    const [sampleTypes, setSampleTypes] = useState([]);
    const [pSetImage, setPSetImage] = useState(new ImageInfoStruct());

    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    
    const history = useHistory();
    const {profileId} = useParams();           // set in the url

    const [profileFromUrl, setProfileFromUrl] = useState(profileId); // we use this to reselect the profile after save
    
    //#region USE_EFFECTS
    useEffect(() => {
        console.debug("refreshing profiles list");
        if(globalState.profiles && globalState.profiles.initialized) {
            setFullProfilesList(globalState.profiles.items);
            setIsDirty(false);
        }
    }, [globalState.profiles]); 
    
    useEffect( () => {
        if(fullProfilesList && fullProfilesList.length>0) {
            setMagProfilesList(fullProfilesList.filter( (item) => item.type===PROFILE_TYPE_MAG)??[]);
            setSampleProfilesList(fullProfilesList.filter( (item) => item.type===PROFILE_TYPE_SAMPLE)??[]);
            doRealListItemClicked(profileFromUrl?Number(profileFromUrl):null);  // preselect if the url has something in it.
        }
    },[fullProfilesList]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(globalState.magTypes && globalState.magTypes.initialized && globalState.magTypes.items.length>0) {
            setMagTypes(globalState.magTypes.items);
        }
        else {
            loadMagTypes();
        }
    },[globalState.magTypes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect( () => {
        if(globalState.sampleTypes && globalState.sampleTypes.initialized && globalState.sampleTypes.items.length>0) {
            setSampleTypes(globalState.sampleTypes.items);
        }
        else {
            loadSampleTypes();
        }
    },[globalState.sampleTypes]); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion USE_EFFECTS
    
    function loadMagTypes() {
        setIsLoading(true);
        
        ArchiverClientApi.sendMagTypesRequest()
        .then( (results) => {
            dispatch(gotMagTypes(results)); // save for possible later use
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsLoading(false));
    }

    function loadSampleTypes() {
        setIsLoading(true);

        ArchiverClientApi.sendSampleTypesRequest()
        .then( (results) => {
            dispatch(gotSampleTypes(results)); // save for possible later use
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsLoading(false));
    }
    
    function getDefaultProfileDetails():{} {
        return(
            {
                id: null,
                name: "",
                comment: "",
                enabled: true,
                subType: null,
                destinationMagProfileId: null,
                barcodesAreUnique: false,
                isCaseMappedExternal: false,
                isSampleNumMappedLater: false,
                noCaseSampleRequired: false,
                inspectionRegions: [],
                sampleImage: {dataBytes:"", numBytes:0}, 
                type: (profileTab==="magprofiles"?PROFILE_TYPE_MAG:PROFILE_TYPE_SAMPLE),
            }
        );
    }
    
    function handleCancelClicked(e) {
        e.preventDefault();
        e.stopPropagation();
        
        confirmClearDirty();
    }
    
    function confirmClearDirty() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetProfileDetailsShown();
                });
        }
        else {
            resetProfileDetailsShown(); // just clear what's there
        }
    }
    
    //#region DELETE
    function handleDeleteProfileClicked(e) {
        e.preventDefault();
        e.stopPropagation();
        
        Popup.showConfirm("Delete Profile?",`<p>This will remove '${curProfileDetails.name}' completely from the system,`+
                                " including any inspection regions and post-processing rules associated with it.</p>"+
                                "<p>Any devices using the profile will continue to use the profile until they are restarted.</p>"+
                                "<p>Are you sure you want to do this?</p>","Yes, Delete it",null, 
            () => {
               doProfileRemove(curProfileDetails.name,curProfileDetails.id);
            });
    }
   
    function doProfileRemove(profileName, profileId) {
        setIsRemoving(true);
        ArchiverClientApi.sendProfileRemove(profileId)
        .then(() => {
            Popup.showInfo(`Profile '${profileName}' removed`);
            dispatch(gotRemoveProfile(profileId));
            resetProfileDetailsShown();
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {
            setIsRemoving(false);
        });
    }
    //#endregion DELETE
    
    //#region SAVE
    function handleSaveClicked(e,action) {
        e.preventDefault();
        e.stopPropagation();

        if (!checkForValid()) {
            return;
        }

        setIsSaving(true);

        // upload our image first if it's changed
        if (pSetImage.dataBytes && pSetImage.hasUnsavedData) { // update image
            CommonClientApi.uploadImageAsSampleImage(curProfileDetails.sampleImage.dataBytes)
            .then((newId: number) => {
                let curImageChanged=new ImageInfoStruct();
                curImageChanged.dataBytes = pSetImage.dataBytes;
                curImageChanged.hasUnsavedData=false;
                setPSetImage(curImageChanged);
                handleProfileSave(action,newId);
            })
            .catch(responseError => ErrorPopup.showError("Upload Problem", `Failed to upload image.  Details: ${responseError.error}.`));
        }
        else {
            handleProfileSave(action);
        }
    }
    
    function handleProfileSave(action:string, newImageId:number=null) {
        
        ArchiverClientApi.sendProfileUpdate(fixValues(), newImageId)
        .then( (savedProfileInfo) => {
            Popup.showInfo(`Profile '${curProfileDetails.name}' Saved`);
            setIsDirty(false);
            clearImageData();
            if(action==="ADD") {
                dispatch(gotAddNewProfile(savedProfileInfo));
            }
            else if(action==="UPDATE") {
                dispatch(gotUpdateProfile(savedProfileInfo));
            }
            setProfileFromUrl(savedProfileInfo.id); // update the url so we can select it
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
           setIsSaving(false); 
        });
    }
    
    function checkForValid():boolean {
        let isValid=true;
        let notValidMsg="";
        if(curProfileDetails.name==="") {
            isValid=false;
            notValidMsg="Name needs to be non-blank";
        }
        if(curProfileDetails.type==="" || curProfileDetails.type===-1) {
            isValid=false;
            notValidMsg="Profile Type needs to be selected";
        }
        if(curProfileDetails.type===PROFILE_TYPE_MAG && (curProfileDetails.subType==="" || curProfileDetails.subType===-1)) {
            isValid=false;
            notValidMsg="Mag Type needs to be selected";
        }
        
        if(!isValid) {
            ErrorPopup.showError("Validation Problem",notValidMsg);
            return false;
        }
        return true;

    }
    
    function fixValues():{} {
        const editInfo = {...curProfileDetails};
        
        if(editInfo.destinationMagProfileId<1) {
            editInfo.destinationMagProfileId=-1; // no destination mag
        }
        
        return editInfo;
    }
    //#endregion SAVE
    
    function onChange(e) {
        const thing = e.target.name;
        const editInfo = {...curProfileDetails};
        if(e.target.type==="checkbox") { // this is a checkbox
            editInfo[thing] = e.target.checked;
            if(thing === "noCaseSampleRequired" && e.target.checked) {
                editInfo.isCaseMappedExternal = false;
                editInfo.isSampleNumMappedLater = false;
                // disable these guys
            }
        }
        else {
            let value = e.target.value;
            
            // both mag and sample subType
            if(thing==="subType") {  // number
                value = Number(value);
            }
           
            // selection drop down
            if(thing==="type") {
                value = Number(value);
            }
            editInfo[thing] = value;
        }
        setCurProfileDetails(editInfo);
        setIsDirty(true);
    }
  
    function handleMagDestProfileChanged(e) {
        const value = e.target.value;
        const editInfo = {...curProfileDetails};
        editInfo.destinationMagProfileId = Number(value); 
        setCurProfileDetails(editInfo);
        setIsDirty(true);
    }

    function showProfilesList(magProfiles:[], sampleProfiles:[]) {

        if(isLoading) {
            return <div className="mt-4 pr-4 w-30 li-border-right-light">...loading...</div>;
        }

        return (
            <div className="mt-5 w-30">
                 {dumpTabHeader()}
                <div className="li-border-right-light">
                   
                    {dumpTabContent(magProfiles, sampleProfiles)}
                </div>
            </div>
        );
    }

    function dumpTabHeader() {
        return (
            <ul className="nav nav-tabs li-border-light-bottom pb-0 mb-0" id="profileTabHeader" role="tablist">
                <li className="nav-item mr-2" role="presentation">
                    <button className={"nav-link " + (profileTab==="magprofiles"?"active":"")} id="magprofiles-tab" onClick={()=>setProfileTab("magprofiles")}>Mag Profiles</button>
                </li>
                <li className="nav-item mr-2" role="presentation">
                    <button className={"nav-link " + (profileTab==="sampleprofiles"?"active":"")} id="sampleprofiles-tab" onClick={()=>setProfileTab("sampleprofiles")}>Samples</button>
                </li>
            </ul>
        );
    }

    function dumpTabContent(magProfiles:[], sampleProfiles:[]) {

        let content:JSX.Element = null;
        if(profileTab==="magprofiles") {
            const magProfileResultList = buildProfileResultList(magProfiles);
            content =
                <div id="magprofiles" className="li-border-light-bottom li-scrollable-at-500 mmb-0" style={{minHeight: "500px"}}>
                    {/* <h4 className="p-3">Mags/Racks</h4> */}
                    <ul className="list-group-flush li-list-group-w-hover pl-0">
                        {magProfileResultList}
                    </ul>
                </div>
            ;
        }
        else if(profileTab==="sampleprofiles") {
            const sampleProfileResultList = buildProfileResultList(sampleProfiles);
            content =
                <div id="sampleprofiles" className="li-border-light-bottom li-scrollable-at-500 mb-0" style={{minHeight: "500px"}}>
                    {/* <h4 className="p-3">Samples</h4> */}
                    <ul className="list-group-flush li-list-group-w-hover pl-0">
                        {sampleProfileResultList}
                    </ul>
                </div>
            ;
        }

        return (
            <>
                {content}
                    <LIButton small primary label="" bgClass={isRemoving?"ml-3 my-3":"fa fa-trash ml-3 my-3"} disabled={curProfileDetails?.id===null}
                          onClick={handleDeleteProfileClicked} showBusy={isRemoving}/>
                    <LIButton small primary label="" bgClass={isRemoving?"":"fa fa-plus m-3"} onClick={confirmClearDirty} showBusy={false}/>
            </>
        );
    }

    function buildProfileResultList(profiles:[]): Element[] {

        profiles.sort( (a,b) => {
            if(a.type<b.type) return -1;
            if(a.type>b.type) return 1;
            return 0;
        });
        let resultList = profiles?.map((item,index) => {
                let extraClass = item.enabled ? "" : " li-strike-through";
                if(curProfileDetails?.id===item.id) {
                    extraClass += " li-bg-light";
                }
                return <li key={index} className={"list-group-item li-pointer "+extraClass}
                           onClick={() => handleListItemClicked(item)}>
                    {item.name}
                </li>;
            }
        );
        if(!resultList || resultList.length===0) {
            resultList = "no profiles found";
        }
        
        return resultList;
    }

    function dumpProfileTypeOptionsList() {
        return PROFILE_TYPES.map(item => <option key={item.type} value={item.type}>{item.name}</option>);
    }

    function dumpMagTypeOptionsList() {
        if(magTypes) {
            return magTypes.map(item => <option key={item.id} value={item.id}>{item.description}</option>);
        }
    }

    function dumpSampleTypeOptionsList() {
        if(sampleTypes) {
            return sampleTypes.map(item => <option key={item.id} value={item.id}>{item.name}</option>);
        }
    }
    
    function handleListItemClicked(item) {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetProfileDetailsShown();
                    doRealListItemClicked(item?.id);
                });
        }
        else {
            doRealListItemClicked(item?.id);
        }
    }
    
    function doRealListItemClicked(itemId) {
        const profile = fullProfilesList.find(i => i.id===itemId);
        if(!profile) {
            resetProfileDetailsShown();
        }
        else {
            setCurProfileDetails(profile);
            const imageInfo = new ImageInfoStruct();
            imageInfo.SetupBasedOnEncodedBytes(profile.sampleImage?.dataBytes); // encoded
            setPSetImage(imageInfo);
            updateUrlLink(`/admin/profiles/${profile.id}`);
        }
    }
    
    function updateUrlLink(toThis) {
        history.replace(toThis);
    }
    
    function resetProfileDetailsShown() {
        setCurProfileDetails(getDefaultProfileDetails());
        clearImageData();
        setIsDirty(false);
        updateUrlLink("/admin/profiles"); // blank

        // focus the first input field
        const nameEl:HTMLInputElement = document.getElementsByName("name")[0];
        if(nameEl) {
            nameEl.focus();
        }
    }
    
    function clearImageData() {
        setPSetImage(new ImageInfoStruct());
    }
    
    function handleEditIRsClicked() {
        const profileId = curProfileDetails.id;
        history.push(`/admin/irs/${profileId}`);
    }
    
    function getIRLabel() {
        const numIRs = curProfileDetails?.inspectionRegions?.length??0;
        return(
            `Inspection Regions (${numIRs})`
        );
    }
    
    function handleBackClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.goBack();
                });
        }
        else {
            history.goBack();
        }
        
    }
    
    function dumpMagTypeListSection() {
        // APPLIES ONLY TO MAG TYPE PROFILES
        if(curProfileDetails.type!==PROFILE_TYPE_MAG) {
            return null;
        }
        return(
            <>
                <label htmlFor="type" className="li-form-label mr-3">Mag Type</label>
                <select onChange={onChange} id="subType" name="subType" value={curProfileDetails.subType??-1}
                    className="li-form-field no-width align-self-end li-border-dark" required={true}>
                    <option value={-1} disabled>(select a type)</option>
                    {dumpMagTypeOptionsList()}
                </select>
                <LIButton label="" small primary faIcon="fa fa-external-link" bgClass="ml-3" onClick={handleGoToMagTypeClicked}/>
                <small className="form-text text-muted">* determines what type of mag this profile applies to</small>
            </>
        );
    }
    
    function handleGoToMagTypeClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.push("/admin/mag_types/"+curProfileDetails.subType);
                });
        }
        else {
            history.push("/admin/mag_types/"+curProfileDetails.subType);
        }
    }

    function handleGoToSampleTypeClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.push("/admin/sample_types/"+curProfileDetails.subType);
                });
        }
        else {
            history.push("/admin/sample_types/"+curProfileDetails.subType);
        }
    }
    
    function dumpDestinationMagProfile() {
        
        // APPLIES ONLY TO SAMPLE TYPE PROFILES
        if(curProfileDetails.type!==PROFILE_TYPE_SAMPLE) {
            return null;
        }
        let destMagProfileValue = curProfileDetails?.destinationMagProfileId??"";
        if(destMagProfileValue===-1 || destMagProfileValue==="-1") {
            destMagProfileValue=""; // stay consistent with "" == none.
        }
        
        const selectedSampleType = sampleTypes.find( item => item.id === curProfileDetails.subType)??{name:`**${curProfileDetails.subType} NOT FOUND**`};
        
        return(

            <div className="">
                <label className="li-form-label mr-3" htmlFor="sample-profile-select">Destination Mag Profile</label>
                <select id="magProfileSelect" className="li-form-field no-width li-border-dark mr-3" name="sample-profile-select" 
                        aria-label="Mag Profile Select"
                        value={destMagProfileValue} onChange={(e)=>handleMagDestProfileChanged(e)}>
                    <option value="-1">any '{selectedSampleType.name}' mag</option>
                    {magProfilesList?.map( (item,index) => {
                        if (item.holdsSampleType===curProfileDetails.subType) {
                            return <option key={index} value={item.id}>{item.name}</option>;
                        }
                        else {
                            return null;
                        }
                    })}
                </select>
                <small className="form-text text-muted">* this list is filtered to include only mag profiles that can hold the above sample type</small>
            </div>
        );
    }

    function dumpSampleTypeListSection() {
        // APPLIES ONLY TO SAMPLE TYPE PROFILES
        if(curProfileDetails.type!==PROFILE_TYPE_SAMPLE) {
            return null;
        }
        return(
            <>
                <label htmlFor="type" className="li-form-label mr-3">Sample Type</label>
                <select onChange={onChange} id="subType" name="subType" value={curProfileDetails.subType??-1}
                        className="li-form-field no-width align-self-end li-border-dark" required={true}>
                    <option value={-1} disabled>(select a type)</option>
                    {dumpSampleTypeOptionsList()}
                </select>
                <LIButton label="" small primary faIcon="fa fa-external-link" bgClass="ml-3" onClick={handleGoToSampleTypeClicked}/>
                <small className="form-text text-muted">* determines what type of sample this profile applies to</small>
            </>
        );
    }
    
    function dumpSampleTypeOptions() {
        // APPLIES ONLY TO SAMPLE TYPE PROFILES
        if(curProfileDetails.type!==PROFILE_TYPE_SAMPLE) {
            return null;
        }
        
        return (<div className="mt-4 card card-body li-bg-light">
                <div className="mt-3">
                    <CheckInput label="Barcodes are unique" name="barcodesAreUnique"
                                checked={curProfileDetails.barcodesAreUnique} onChanged={(e) => onChange(e)}
                                helpLink="admin/profile_admin.html#barcodes_are_unique"
                                helpText="* samples missing case or sample # will generate duplicates if barcodes are unique"/>
                </div>
                <div className="mt-3">
                    <CheckInput label="Case and sample are mapped externally" name="isCaseMappedExternal"
                                checked={curProfileDetails.isCaseMappedExternal} onChanged={(e) => onChange(e)}
                                disabled={curProfileDetails.noCaseSampleRequired}
                                helpLink="admin/profile_admin.html#is_case_mapped_external"
                                helpText="* The external SlideMapper will need to be configured to watch for this profile too!"/>
                </div>
                <div className="mt-3">
                    <CheckInput label="Sample # can be missing until retrieval" name="isSampleNumMappedLater"
                                checked={curProfileDetails.isSampleNumMappedLater} onChanged={(e) => onChange(e)}
                                disabled={curProfileDetails.noCaseSampleRequired}
                                helpLink="admin/profile_admin.html#is_samplenum_mapped_later"
                                helpText="* the sample # will need to be manually added prior to any retrievals"/>
                </div>
                <div className="mt-3">
                    <CheckInput label="No case or sample # ever required" name="noCaseSampleRequired"
                                checked={curProfileDetails.noCaseSampleRequired} onChanged={(e) => onChange(e)}
                                helpLink="admin/profile_admin.html#no_case_sample_required" 
                                helpText="* the sample will never need a case or sample # (it has a barcode only)."/>
                </div>
            </div>
        );
        
    }

    function setNewImageInfo(dataBytes) {

        const updatedImage = new ImageInfoStruct(dataBytes, true, null);
        setPSetImage(updatedImage);

        // and the profile is actually dirty now too
        const editInfo = {...curProfileDetails};
        editInfo.sampleImage = {
            dataBytes: updatedImage.encodedBytes, numBytes: updatedImage.encodedBytes.length
        };
        setCurProfileDetails(editInfo);
        setIsDirty(true);
    }

    //#region EXPORT/IMPORT
    function handleProfileExport() {
        // grab the report from the server as JSON (this is the exact profile from the db)
        ArchiverClientApi.sendProfileExport(curProfileDetails.id)
        .then((profileJson) => {
            saveProfileJsonToFile(profileJson, curProfileDetails.name);
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError));
    }
    
    function handleProfileImport() {
        // show the file dialog
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = ".json";
        fileInput.onchange = (e) => handleFileSelected(e);
        fileInput.click();
    }
    
    function handleFileSelected(e) {
        const file = e.target.files[0];
        if(file) {
            const reader = new FileReader();
            reader.onload = (e) => handleFileLoaded(e);
            reader.readAsText(file);
        }
    }
    
    function handleFileLoaded(e) {
        const fileText = e.target.result;
        const profileJson = JSON.parse(fileText);
        const profileName = profileJson.Name;
        
        // show a confirm dialog
        Popup.showConfirm("Import Profile?",
            `<p class="li-font-medium">This will import the profile '<b>${profileName}</b>' into the system.</p>`+
            "<p class='li-font-medium'>*NOTE: this creates a NEW profile along with its inspection regions and processing rules.</p>"+
            "<p class='li-font-medium'>*NOTE2: Sample Types and Mag Types need to be in the manually created before importing profiles.</p>",
            "Yes, Import it",null,
            () => {
                doProfileImport(profileName, profileJson);
            },null,null, "Cancel");
    }
    
    function doProfileImport(profileName, profileJson) {
        setIsSaving(true);
        ArchiverClientApi.sendProfileImport(profileName, profileJson)
        .then((newProfile) => {
            Popup.showInfo(`Profile '${newProfile.name}' imported`);
            dispatch(gotAddNewProfile(newProfile));
            resetProfileDetailsShown();
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {
            setIsSaving(false);
        });
    }
    
    function saveProfileJsonToFile(profileJson, profileName) {
        
        // remove the id's from the profile (so they don't mess up anything already in there on import
        
        /*
         * borrowed and modified from:
         *     https://stackoverflow.com/questions/32326973/file-write-operation-in-javascript
         */

        const dateParts = LIUtils.getParsedDate(new Date());
        const dateExtension = `${dateParts.year-2000}${dateParts.fullMonth}${dateParts.fullDate}`;
        const typeExtension = curProfileDetails.type===PROFILE_TYPE_MAG?"mag":"sample";
        const downloadFileName = "profile-"+typeExtension+"-"+profileName.replace(/ /g, "_")+"-"+dateExtension+".json";
        
        // hidden link
        const link = document.createElement("a");
        link.download = downloadFileName;

        const blob = new Blob([profileJson], {type: 'application/json'});
        link.href = window.URL.createObjectURL(blob);
        document.body.appendChild(link);

        // activate
        link.click();

        // cleanup
        setTimeout(() => { // remove the link
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
        }, 1000);
    }
    //#endregion EXPORT/IMPORT
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="row li-header-row justify-content-between">
                <div>
                    <BackButton onClick={handleBackClicked}/>
                    <h1 className="ml-4 d-inline-block align-middle">Profile Administration</h1>
                </div>
                <div className="align-self-center">
                    <LIButton bgClass="mr-4" small name="importBtn" label="Import" secondary faIcon="ml-1 fas fa-upload"
                              disabled={isDirty}
                              tooltip="Import A Profile" onClick={handleProfileImport}/>
                    <LIButton bgClass="mr-4" small name="exportBtn" faIcon="fas fa-download" primary
                              disabled={isDirty||curProfileDetails?.id===null}
                              tooltip="Export This Profile" onClick={handleProfileExport}/>
                </div>
            </div>
            
            <div className="row">
                {/* LEFT SIDE PICK LIST */}
                {showProfilesList(magProfilesList, sampleProfilesList)}

                {/* RIGHT SIDE FORM */}
                <div className="pl-4 w-60">
                    <div className="li-fg-slightly-muted li-font-small text-right">
                        [Id:{curProfileDetails?.id ?? "-"}]
                    </div>
                   
                    <div className="row m-0 p-0 justify-content-end mt-2">
                        <CheckInput label="Enabled" name="enabled" checked={curProfileDetails.enabled}
                                    onChanged={(e) => onChange(e)}/>
                    </div>
                    <div className="row p-0 m-0 justify-content-between">
                        {/* right side, row #1 */}
                        <TextInput name="name" label="Name" onChange={(e) => onChange(e)}
                                   placeholder="" textLeft maxLength={32}
                                   value={curProfileDetails?.name ?? ""}
                                   required/>
                        <div className="align-self-end">
                            <label htmlFor="type" className="li-form-label mx-2">Profile Type</label>
                            <select onChange={onChange} id="type" name="type" value={curProfileDetails.type ?? -1}
                                    className="li-form-field no-width align-self-end li-border-dark" required={true}>
                                <option value={-1} disabled>(select a type)</option>
                                {dumpProfileTypeOptionsList()}
                            </select>
                        </div>
                    </div>
                    {/* right side, row #2 */}
                    <TextArea name="comment" label="Notes" value={curProfileDetails?.comment ?? ""} readonly={false}
                              onChange={(e) => onChange(e)} rows={2} maxLength={60}/>

                    {/* right side, row #3 */}
                    {dumpSampleTypeListSection()}
                    {dumpDestinationMagProfile()}
                    {dumpSampleTypeOptions()}

                    {dumpMagTypeListSection()}

                    <div className="">
                        <label className="li-form-label mr-3">Profile Image</label>
                        <ImageSetterCard disabled={isSaving || curProfileDetails.name === ""}
                                         imageInfo={pSetImage} onSaveClicked={setNewImageInfo}/>
                    </div>

                    {/*#region INSPECTION_REGIONS */}
                    <div className="my-5"/>
                    <div className="text-center">
                        <LIButton name="inspection_regions" label={getIRLabel()} bgClass="w-75" secondary
                                  disabled={curProfileDetails?.id === null || isDirty}
                                  onClick={handleEditIRsClicked}/>
                    </div>
                    {/*endregion INSPECTION_REGIONS*/}

                    {/* BUTTON ROW */}
                    <div className="text-center my-5">
                        <LIButton key="cancel" name="cancel" label={isDirty ? "Cancel" : "Clear"} bgClass="mr-4" primary
                                  onClick={handleCancelClicked}/>
                        <LIButton key="save" name="save" label={curProfileDetails?.id === null ? "ADD" : "UPDATE"}
                                  secondary disabled={!isDirty || isSaving}
                                  onClick={(e) => handleSaveClicked(e, curProfileDetails?.id === null ? "ADD" : "UPDATE")}
                                  showBusy={isSaving}/>
                    </div>
                </div>
            </div>


        </div>);
}

ProfileAdminPage.propTypes = {};

export default ProfileAdminPage;